(() => {
  if (customElements.get("wc-flipbook")) return;

  // Shared by instances; fetched only if PDF.js needs an auxiliary resource.
  const resourceBundles = new Map();
  function resourceFactory(url) {
    return class {
      async fetch({ filename }) {
        if (!resourceBundles.has(url)) {
          const promise = fetch(url)
            .then((response) => {
              if (!response.ok) throw new Error("PDF resources unavailable");
              return response.json();
            })
            .catch((error) => {
              resourceBundles.delete(url);
              throw error;
            });
          resourceBundles.set(url, promise);
        }
        const bundle = await resourceBundles.get(url);
        if (!bundle[filename])
          throw new Error(`Missing PDF resource: ${filename}`);
        return Uint8Array.from(atob(bundle[filename]), (char) =>
          char.charCodeAt(0),
        );
      }
    };
  }

  class WcFlipbook extends HTMLElement {
    connectedCallback() {
      if (this.session) return;
      this.querySelector("[data-status]").textContent = "Loading publication…";
      this.querySelector("[data-counter]").textContent = "Page — / —";
      this.querySelector("[data-prev]").disabled = true;
      this.querySelector("[data-next]").disabled = true;
      this.querySelector("[data-fullscreen]").hidden = true;
      this.querySelector("[data-placeholder]").hidden = false;
      const session = {
        abort: new AbortController(),
        pages: [],
        wanted: [],
        stopped: false,
      };
      this.session = session;
      session.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          session.observer.disconnect();
          this.initialize(session);
        },
        { rootMargin: "300px" },
      );
      session.observer.observe(this);
    }

    disconnectedCallback() {
      const session = this.session;
      if (!session) return;
      session.stopped = true;
      session.abort.abort();
      session.observer.disconnect();
      session.resize?.disconnect();
      clearTimeout(session.resizeTimer);
      session.renderTask?.cancel();
      session.flip?.destroy();
      session.loading?.destroy().catch(() => {});
      session.pages.forEach((page) => {
        if (page.url) URL.revokeObjectURL(page.url);
      });
      this.session = null;
    }

    async initialize(session) {
      const status = this.querySelector("[data-status]");
      try {
        const [pdfjs, { PageFlip }] = await Promise.all([
          import(this.dataset.pdfLibrary),
          import(this.dataset.flipLibrary),
        ]);
        if (session.stopped) return;
        pdfjs.GlobalWorkerOptions.workerSrc = this.dataset.pdfWorker;
        session.loading = pdfjs.getDocument({
          url: this.dataset.pdfUrl,
          isEvalSupported: false,
          // Fetch only requested ranges when the PDF host supports them.
          disableAutoFetch: true,
          disableStream: true,
          useWorkerFetch: false,
          BinaryDataFactory: resourceFactory(this.dataset.pdfResources),
        });
        session.pdf = await session.loading.promise;
        if (session.stopped) return;
        const firstPage = await session.pdf.getPage(1);
        if (session.stopped) return;
        const viewport = firstPage.getViewport({ scale: 1 });
        session.ratio = viewport.width / viewport.height;
        const stage = this.querySelector("[data-stage]");
        // destroy() removes the book element; recreate it on reconnection.
        this.querySelector("[data-book]")?.remove();
        const book = document.createElement("div");
        book.className = "wc-flipbook__book";
        book.dataset.book = "";
        stage.append(book);
        for (let number = 1; number <= session.pdf.numPages; number++) {
          const element = document.createElement("div");
          element.className = "wc-flipbook__page";
          const label = document.createElement("span");
          label.className = "wc-flipbook__page-label";
          label.textContent = `Loading page ${number}…`;
          const image = document.createElement("img");
          image.alt = `Page ${number} of ${session.pdf.numPages}. Download the PDF for accessible text.`;
          image.hidden = true;
          image.draggable = false;
          const links = document.createElement("div");
          links.className = "wc-flipbook__links";
          links.hidden = true;
          element.append(label, image, links);
          book.append(element);
          session.pages.push({ element, label, image, links, number });
        }
        session.flip = new PageFlip(book, {
          width: 500,
          height: Math.round(500 / session.ratio),
          size: "stretch",
          minWidth: session.pdf.numPages === 1 ? 10000 : 375,
          maxWidth: 1600,
          minHeight: 100,
          maxHeight: 2200,
          autoSize: false,
          usePortrait: true,
          showCover: true,
          maxShadowOpacity: 0.25,
          flippingTime: matchMedia("(prefers-reduced-motion: reduce)").matches
            ? 1
            : 650,
          mobileScrollSupport: true,
          useMouseEvents: true,
          clickEventForward: true,
          showPageCorners: true,
          disableFlipByClick: false,
        });
        const refresh = () => this.refresh(session);
        session.flip.on("flip", refresh);
        session.flip.on("changeOrientation", refresh);
        session.flip.on("changeState", (event) => {
          if (event.data === "read") refresh();
        });
        session.flip.loadFromHTML(session.pages.map((page) => page.element));
        // showCover preserves spread pairing but marks the first and any
        // unpaired final page as hard. Publications should curl like paper,
        // including their covers; reset density after the spreads are built.
        session.pages.forEach((page, index) => {
          session.flip.getPage(index).setDensity("soft");
        });
        this.querySelector("[data-placeholder]").hidden = true;
        const options = { signal: session.abort.signal };
        // Delegate on the stage so links in StPageFlip's cloned pages work too.
        // Preserve the anchor's native click/tap/keyboard behavior, but keep
        // its gestures away from the library's page-turn handlers.
        for (const type of [
          "mousedown",
          "mouseup",
          "touchstart",
          "touchend",
          "click",
        ]) {
          stage.addEventListener(
            type,
            (event) => {
              if (event.target.closest(".wc-flipbook__link"))
                event.stopPropagation();
            },
            { ...options, capture: true },
          );
        }
        const turn = (direction) => {
          if (session.flip.getState() !== "read") return;
          if (direction > 0) session.flip.flipNext();
          else session.flip.flipPrev();
        };
        this.querySelector("[data-prev]").addEventListener(
          "click",
          () => turn(-1),
          options,
        );
        this.querySelector("[data-next]").addEventListener(
          "click",
          () => turn(1),
          options,
        );
        stage.addEventListener(
          "keydown",
          (event) => {
            if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
            event.preventDefault();
            turn(event.key === "ArrowRight" ? 1 : -1);
          },
          options,
        );
        const viewer = this.querySelector("[data-viewer]");
        const fullscreen = this.querySelector("[data-fullscreen]");
        fullscreen.hidden =
          !document.fullscreenEnabled || !viewer.requestFullscreen;
        fullscreen.addEventListener(
          "click",
          async () => {
            try {
              if (document.fullscreenElement === viewer)
                await document.exitFullscreen();
              else await viewer.requestFullscreen();
            } catch (error) {
              status.textContent =
                "Fullscreen is unavailable in this browser or preview.";
            }
          },
          options,
        );
        document.addEventListener(
          "fullscreenchange",
          () => {
            fullscreen.textContent =
              document.fullscreenElement === viewer
                ? "Exit fullscreen"
                : "Fullscreen";
          },
          options,
        );
        session.resize = new ResizeObserver(() => {
          clearTimeout(session.resizeTimer);
          session.resizeTimer = setTimeout(() => {
            if (session.stopped) return;
            session.flip.update();
            refresh();
          }, 150);
        });
        session.resize.observe(stage);
        refresh();
      } catch (error) {
        if (session.stopped) return;
        status.textContent =
          "This publication could not be loaded. Use Download PDF to open it.";
        console.warn("WC Flipbook: unable to load publication.", error);
      }
    }

    refresh(session) {
      if (session.stopped || !session.flip) return;
      const index = session.flip.getCurrentPageIndex();
      // The cover stands alone; interior spreads begin with PDF pages 2–3.
      const spread = session.flip.getOrientation() === "landscape" && index > 0;
      const end = Math.min(index + (spread ? 2 : 1), session.pages.length);
      this.querySelector("[data-counter]").textContent =
        spread && end > index + 1
          ? `Pages ${index + 1}–${end} / ${session.pages.length}`
          : `Page ${index + 1} / ${session.pages.length}`;
      this.querySelector("[data-prev]").disabled = index === 0;
      this.querySelector("[data-next]").disabled = end === session.pages.length;
      session.visible = [index, end];
      session.wanted = [];
      // Visible pages first, followed by the next and previous spread.
      for (let i = index; i < Math.min(end + 2, session.pages.length); i++)
        session.wanted.push(i);
      for (let i = Math.max(0, index - 2); i < index; i++)
        session.wanted.push(i);
      const bounds = session.flip.getBoundsRect();
      session.pixelWidth = Math.max(
        1,
        Math.round(
          Math.min(bounds.pageWidth * Math.min(devicePixelRatio || 1, 2), 1800),
        ),
      );
      if (session.flip.getState() === "read") {
        for (let i = 0; i < session.pages.length; i++) {
          const page = session.pages[i];
          if (session.wanted.includes(i) || !page.url) continue;
          page.image.hidden = true;
          page.links.hidden = true;
          page.image.removeAttribute("src");
          URL.revokeObjectURL(page.url);
          page.url = null;
          page.width = 0;
          page.label.hidden = false;
        }
      }
      this.renderPages(session);
    }

    async renderLinks(session, page, pdfPage, viewport) {
      if (page.linksLoaded) return;
      try {
        const annotations = await pdfPage.getAnnotations({ intent: "display" });
        if (session.stopped) return;
        // Match the image's object-fit: contain area, including differently
        // sized or rotated pages. Percent coordinates survive resizing/fullscreen.
        const bookRatio = 500 / Math.round(500 / session.ratio);
        const imageRatio = viewport.width / viewport.height;
        const width = Math.min(1, imageRatio / bookRatio);
        const height = Math.min(1, bookRatio / imageRatio);
        Object.assign(page.links.style, {
          width: `${width * 100}%`,
          height: `${height * 100}%`,
          left: `${(1 - width) * 50}%`,
          top: `${(1 - height) * 50}%`,
        });
        for (const annotation of annotations) {
          if (
            annotation.subtype !== "Link" ||
            !annotation.url ||
            annotation.annotationFlags & 35
          )
            continue;
          // Never use unsafeUrl or execute PDF actions/scripts.
          let url;
          try {
            url = new URL(annotation.url);
          } catch {
            continue;
          }
          if (!["http:", "https:", "mailto:", "tel:"].includes(url.protocol))
            continue;
          if (
            !Array.isArray(annotation.rect) ||
            annotation.rect.length !== 4 ||
            !annotation.rect.every(Number.isFinite)
          )
            continue;
          // PDF.js 6 exposes the viewport transform rather than the older
          // convertToViewportRectangle helper. Transform opposite corners
          // from PDF coordinates (bottom-left origin) into image coordinates.
          const [a, b, c, d, e, f] = viewport.transform;
          const [x1, y1, x2, y2] = annotation.rect;
          const rect = [
            a * x1 + c * y1 + e,
            b * x1 + d * y1 + f,
            a * x2 + c * y2 + e,
            b * x2 + d * y2 + f,
          ];
          const left = Math.max(0, Math.min(rect[0], rect[2]));
          const top = Math.max(0, Math.min(rect[1], rect[3]));
          const right = Math.min(viewport.width, Math.max(rect[0], rect[2]));
          const bottom = Math.min(viewport.height, Math.max(rect[1], rect[3]));
          if (right <= left || bottom <= top) continue;
          const link = document.createElement("a");
          link.className = "wc-flipbook__link";
          link.href = url.href;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.draggable = false;
          const label = annotation.contentsObj?.str || url.href;
          link.setAttribute("aria-label", `${label} (opens in a new tab)`);
          link.title = `${label} (opens in a new tab)`;
          Object.assign(link.style, {
            left: `${(left / viewport.width) * 100}%`,
            top: `${(top / viewport.height) * 100}%`,
            width: `${((right - left) / viewport.width) * 100}%`,
            height: `${((bottom - top) / viewport.height) * 100}%`,
          });
          page.links.append(link);
        }
        page.linksLoaded = true;
      } catch (error) {
        // A damaged annotation should not prevent the page itself rendering.
        console.warn(
          `WC Flipbook: links on page ${page.number} could not be loaded.`,
          error,
        );
      }
    }

    async renderPages(session) {
      if (session.rendering || session.stopped) return;
      session.rendering = true;
      try {
        while (!session.stopped) {
          const index = session.wanted.find((i) => {
            const page = session.pages[i];
            return (
              !page.failed &&
              (!page.url || Math.abs(page.width - session.pixelWidth) > 100)
            );
          });
          if (index === undefined) break;
          const page = session.pages[index];
          const width = session.pixelWidth;
          let pdfPage;
          let canvas;
          try {
            pdfPage = await session.pdf.getPage(page.number);
            if (session.stopped) break;
            const base = pdfPage.getViewport({ scale: 1 });
            await this.renderLinks(session, page, pdfPage, base);
            if (session.stopped) break;
            // Cap a single raster at ~2 million pixels, including tall pages.
            const scale = Math.min(
              width / base.width,
              Math.sqrt(2000000 / (base.width * base.height)),
            );
            const viewport = pdfPage.getViewport({ scale });
            canvas = document.createElement("canvas");
            canvas.width = Math.ceil(viewport.width);
            canvas.height = Math.ceil(viewport.height);
            session.renderTask = pdfPage.render({
              canvasContext: canvas.getContext("2d"),
              viewport,
            });
            await session.renderTask.promise;
            const blob = await new Promise((resolve) =>
              canvas.toBlob(resolve, "image/png"),
            );
            if (session.stopped) break;
            if (!blob) throw new Error("Unable to create page image");
            if (!session.wanted.includes(index)) continue;
            const url = URL.createObjectURL(blob);
            if (page.url) URL.revokeObjectURL(page.url);
            page.url = url;
            page.width = width;
            // Images, unlike canvases, survive StPageFlip's portrait-mode clones.
            page.image.src = url;
            page.image.hidden = false;
            page.links.hidden = false;
            page.label.hidden = true;
          } catch (error) {
            if (session.stopped) break;
            page.failed = true;
            page.label.textContent = `Page ${page.number} could not be displayed. Please open the PDF.`;
            console.warn(`WC Flipbook: page ${page.number} failed.`, error);
          } finally {
            if (canvas) {
              canvas.width = 0;
              canvas.height = 0;
            }
            pdfPage?.cleanup();
            session.renderTask = null;
          }
        }
      } finally {
        session.rendering = false;
        if (!session.stopped) {
          const visible = session.pages.slice(...session.visible);
          this.querySelector("[data-status]").textContent = visible.some(
            (page) => page.failed,
          )
            ? "Some pages could not be displayed. Use Download PDF to read the original."
            : visible.every((page) => page.url)
              ? ""
              : "Loading pages…";
        }
      }
    }
  }
  customElements.define("wc-flipbook", WcFlipbook);
})();
